import classnames from 'classnames';

export type LoadingDotsColor = 'black' | 'white' | 'gray' | 'gradient';

const GRADIENT_ANIMATION_DURATION_MS = 2500;
const GRADIENT_BACKGROUND_SIZE = '200% 100%';
const GRADIENT_PHASE_OFFSETS_MS = [
  0,
  -GRADIENT_ANIMATION_DURATION_MS / 3,
  (-2 * GRADIENT_ANIMATION_DURATION_MS) / 3
] as const;

interface LoadingDotsProps {
  color?: LoadingDotsColor;
  transparent?: boolean;
  dotSize?: 'x-small' | 'small' | 'large';
  animateGradient?: boolean;
}

const LoadingDots: React.FC<LoadingDotsProps> = ({
  color = 'white',
  dotSize = 'large',
  animateGradient = false
}) => {
  const dotsContainerClasses = classnames('flex justify-center');

  const gradientSpanClasses = 'bg-gradient-full';

  const dotClasses = classnames('mx-[2px] animate-bounce rounded-full', {
    'my-[9px] size-[7px]': dotSize === 'large',
    'my-[10px] size-1': dotSize === 'small',
    'my-[12px] size-0.5': dotSize === 'x-small',
    'bg-white': color === 'white',
    'bg-gray-darker': color === 'black',
    'bg-gray-medium': color === 'gray',
    [gradientSpanClasses]: color === 'gradient' && !animateGradient,
    'overflow-hidden': color === 'gradient' && animateGradient
  });

  const firstDotClasses = classnames(dotClasses, {
    'bg-dot': color === 'white',
    'bg-[position:0%_0%]': color === 'gradient' && !animateGradient
  });
  const secondDotClasses = classnames(dotClasses, {
    'bg-dot-dark': color === 'white',
    'bg-center': color === 'gradient' && !animateGradient
  });
  const thirdDotClasses = classnames(dotClasses, {
    'bg-dot-darker': color === 'white',
    'bg-[position:100%_0%]': color === 'gradient' && !animateGradient
  });

  const animatedGradient = color === 'gradient' && animateGradient;

  const gradientLayer = (animationDelayMs: number) =>
    animatedGradient ? (
      <span
        className="block size-full animate-gradient-slide bg-gradient-full motion-reduce:animate-none"
        style={{
          animationDelay: `${animationDelayMs}ms`,
          animationDuration: `${GRADIENT_ANIMATION_DURATION_MS}ms`,
          backgroundSize: GRADIENT_BACKGROUND_SIZE
        }}
      />
    ) : null;

  const getDotStyle = (animationDelay?: string) => ({
    ...(animationDelay ? { animationDelay } : {}),
    ...(color === 'gradient' && !animateGradient
      ? { backgroundSize: GRADIENT_BACKGROUND_SIZE }
      : {})
  });

  return (
    <span role="presentation" className={dotsContainerClasses}>
      <span style={getDotStyle()} className={firstDotClasses}>
        {gradientLayer(GRADIENT_PHASE_OFFSETS_MS[0])}
      </span>
      <span style={getDotStyle('200ms')} className={secondDotClasses}>
        {gradientLayer(GRADIENT_PHASE_OFFSETS_MS[1])}
      </span>
      <span style={getDotStyle('400ms')} className={thirdDotClasses}>
        {gradientLayer(GRADIENT_PHASE_OFFSETS_MS[2])}
      </span>
    </span>
  );
};

export default LoadingDots;
